import React from "react";
import { Phone, CalendarCheck, Car, CreditCard, MapPin, ClipboardCheck } from "lucide-react";
import { motion } from "framer-motion";
import { SCHOOL } from "@/lib/constants";

const EASE = [0.22, 1, 0.36, 1];

const HERO_RATES = [
  {
    label: "G & G2 Lesson",
    tiers: [
      { value: "$65", unit: "regular price per hour" },
      { value: "$60", unit: "per hour with package" },
    ],
    description:
      "One-hour G or G2 driving lesson. Get more lessons for extra discount.",
  },
  {
    label: "Local Road Test (GTA)",
    tiers: [
      { value: "$200", unit: "car for road test" },
    ],
    description:
      "Dual-brake car for road test. Pick up and drop off included within 4-5 km; otherwise extra charge.",
  },
  {
    label: "Outside GTA",
    tiers: [
      { value: "$50-$200", unit: "extra charge" },
    ],
    description:
      "Outside-GTA road-test service has an extra charge based on distance.",
  },
  {
    label: "Emergency Lesson",
    tiers: [
      { value: "$70", unit: "per hour" },
    ],
    description:
      "Emergency lesson rate for urgent extra practice.",
  },
];

const HERO_OFFERS = [
  {
    icon: CalendarCheck,
    eyebrow: "7 days a week",
    title: "Classes run 6 AM to 10 PM",
    body: `Call or text ${SCHOOL.phoneDisplay} to book your class by appointment.`,
  },
  {
    icon: CalendarCheck,
    eyebrow: "$50",
    title: "Early date available",
    body: "Early road test date available for $50.",
  },
  {
    icon: ClipboardCheck,
    eyebrow: "$20",
    title: "Mock test before the road test",
    body: "Mock test just before the road test at just $20.",
  },
  {
    icon: CreditCard,
    eyebrow: "Payment",
    title: "Cash, debit, credit card & Amex",
    body: "Payment can be done by cash, debit or credit card and Amex card.",
  },
  {
    icon: Car,
    eyebrow: "Pick up & drop off",
    title: "Included for lessons",
    body: "Downtown pick and drop off is extra. Lessons are also available in countryside.",
  },
  {
    icon: MapPin,
    eyebrow: "Area covered",
    title: "GTA & surrounding area covered",
    body: "Local service covers GTA and surrounding areas; outside-GTA extra charge is based on distance.",
  },
];

export const Hero = () => {
  return (
    <section id="home" data-testid="section-hero" className="relative overflow-hidden">
      <div className="hero-corner-wash" />
      <div className="noise-overlay" />
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pt-10 sm:pt-14 pb-10 sm:pb-14">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, ease: EASE }}
        >
          <div>
            <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl text-maple-navy tracking-[-0.02em] leading-[1.05] text-balance">
              Drive today.
              <br />
              <span className="text-maple-brown italic">Succeed tomorrow.</span>
            </h1>

            {/* Phone */}
            <div className="mt-8">
              <a
                href={SCHOOL.phoneLink}
                className="inline-flex items-center gap-2 text-maple-navy hover:text-maple-tan2 transition-colors"
                data-testid="hero-phone-link"
              >
                <span className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-maple-tan2/15 text-maple-tan2">
                  <Phone className="h-4 w-4" aria-hidden="true" />
                </span>
                <span className="text-xs sm:text-sm font-semibold tracking-tight">
                  {SCHOOL.phoneDisplay}
                </span>
              </a>
            </div>

            {/* Hourly rates */}
            <div
              className="mt-10 grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4"
              data-testid="hero-rates"
            >
              {HERO_RATES.map((r, i) => (
                <motion.div
                  key={r.label}
                  initial={{ opacity: 0, y: 12 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-40px" }}
                  transition={{ duration: 0.5, delay: i * 0.08, ease: EASE }}
                  className="rounded-2xl glass-card p-4 sm:p-5"
                  data-testid={`hero-rate-card-${i}`}
                >
                  <span className="text-[11px] font-semibold uppercase tracking-[0.18em] text-maple-brown">
                    {r.label}
                  </span>
                  <div className="mt-3 space-y-2">
                    {r.tiers.map((t, ti) => (
                      <div key={ti} className="flex items-baseline gap-2">
                        <span className="font-serif text-3xl sm:text-4xl text-maple-navy tracking-[-0.02em] leading-none">
                          {t.value}
                        </span>
                        <span className="text-xs sm:text-sm text-maple-navy/75">{t.unit}</span>
                      </div>
                    ))}
                  </div>
                  <p className="mt-3 text-xs sm:text-sm text-maple-navy/75 leading-relaxed">
                    {r.description}
                  </p>
                </motion.div>
              ))}
            </div>

            {/* Offers */}
            <div
              className="mt-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4"
              data-testid="hero-offers"
            >
              {HERO_OFFERS.map((o, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 12 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-40px" }}
                  transition={{ duration: 0.5, delay: i * 0.08, ease: EASE }}
                  className="rounded-2xl glass-card p-4 sm:p-5 flex items-start gap-3"
                  data-testid={`hero-offer-card-${i}`}
                >
                  <span className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-maple-tan2/10 text-maple-tan2">
                    <o.icon className="h-5 w-5" aria-hidden="true" />
                  </span>
                  <div className="min-w-0">
                    <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-maple-tan2">
                      {o.eyebrow}
                    </p>
                    <p className="mt-0.5 font-semibold text-maple-navy text-sm leading-tight">
                      {o.title}
                    </p>
                    <p className="mt-1 text-xs text-maple-navy/75 leading-relaxed">
                      {o.body}
                    </p>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};
